import React, { useState, useCallback, useRef, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  RadioGroup, 
  FormControlLabel, 
  Radio, 
  Paper,
  Chip,
  IconButton,
  Tooltip
} from '@material-ui/core';
import { 
  AttachFile, 
  FormatBold, 
  FormatItalic, 
  FormatUnderline,
  Visibility
} from '@material-ui/icons';
import RichTextAnswer from './RichTextAnswer';
import AuthenticatedImage from './AuthenticatedImage';
import './ScenarioTextAnswer.css';

const ScenarioTextAnswer = ({ 
  question, 
  answer, 
  onAnswerChange, 
  disabled = false,
  examCode,
  userId 
}) => {
  const [selectedScenario, setSelectedScenario] = useState(answer?.selectedScenario || '');
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = useRef(null);

  // Update answer when scenario selection changes
  useEffect(() => {
    if (selectedScenario !== answer?.selectedScenario) {
      const updatedAnswer = {
        ...answer,
        selectedScenario,
        text: answer?.text || ''
      };
      onAnswerChange(question.id, updatedAnswer);
    }
  }, [selectedScenario, answer, onAnswerChange, question.id]);

  // Handle scenario selection
  const handleScenarioChange = (event) => {
    setSelectedScenario(event.target.value);
  };

  // Handle text answer change
  const handleTextChange = useCallback((questionId, textAnswer) => {
    const updatedAnswer = {
      ...answer,
      selectedScenario,
      text: textAnswer.text || '',
      images: textAnswer.images || []
    };
    onAnswerChange(questionId, updatedAnswer);
  }, [answer, selectedScenario, onAnswerChange]);

  // Get evaluation points for selected scenario (for backend use only)
  const getEvaluationPoints = () => {
    if (!selectedScenario || !question.scenarioOptions) return [];
    const scenario = question.scenarioOptions.find(opt => opt.title === selectedScenario);
    return scenario?.evaluationPoints || [];
  };

  const evaluationPoints = getEvaluationPoints();

  return (
    <Box className="scenario-text-answer">
      {/* Question Image */}
      {question.imageUrl && (
        <Paper elevation={1} style={{ marginBottom: 16, padding: 16 }}>
          <Typography variant="h6" style={{ marginBottom: 8, color: '#1976d2' }}>
            🖼️ Question Image
          </Typography>
          <AuthenticatedImage 
            imageUrl={question.imageUrl}
            questionId={question.id}
            alt="Question" 
            style={{ 
              maxHeight: 650, // 30% larger image for better visibility
              width: '100%'
            }} 
          />
        </Paper>
      )}
      
      {/* Scenario Selection */}
      <Paper elevation={1} className="scenario-selection">
        <Typography variant="h6" className="scenario-title">
          Choose an Option:
        </Typography>
        <RadioGroup
          value={selectedScenario}
          onChange={handleScenarioChange}
          disabled={disabled}
        >
          {/* Debug: Show scenario options count */}
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
            Available options: {question.scenarioOptions?.length || 0}
          </Typography>
          {question.scenarioOptions?.map((option, index) => (
            <FormControlLabel
              key={index}
              value={option.title}
              control={<Radio color="primary" />}
              label={
                <Box>
                  <Typography variant="body1" className="scenario-option-title" style={{ fontWeight: 600, marginBottom: 8 }}>
                    {option.title}
                  </Typography>
                  {option.content && (
                    <Box style={{ marginLeft: 16 }}>
                      {option.content.split('\n').map((line, lineIndex) => {
                        const trimmedLine = line.trim();
                        if (!trimmedLine) return null;
                        
                        // Check if line starts with bullet point indicators
                        if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-') || trimmedLine.startsWith('●')) {
                          return (
                            <Typography 
                              key={lineIndex} 
                              variant="body2" 
                              style={{ 
                                marginBottom: 4, 
                                color: '#555',
                                lineHeight: 1.4
                              }}
                            >
                              ● {trimmedLine.replace(/^[•\-●]\s*/, '')}
                            </Typography>
                          );
                        } else if (trimmedLine.toLowerCase().includes('should:') || trimmedLine.toLowerCase().includes('email should:')) {
                          return (
                            <Typography 
                              key={lineIndex} 
                              variant="body2" 
                              style={{ 
                                marginBottom: 8, 
                                color: '#333',
                                fontWeight: 500,
                                lineHeight: 1.4
                              }}
                            >
                              {trimmedLine}
                            </Typography>
                          );
                        } else {
                          return (
                            <Typography 
                              key={lineIndex} 
                              variant="body2" 
                              style={{ 
                                marginBottom: 4, 
                                color: '#555',
                                lineHeight: 1.4
                              }}
                            >
                              {trimmedLine}
                            </Typography>
                          );
                        }
                      })}
                    </Box>
                  )}
                </Box>
              }
              className="scenario-option"
            />
          ))}
          {/* Fallback test options if none are loaded */}
          {(!question.scenarioOptions || question.scenarioOptions.length === 0) && (
            <>
              <FormControlLabel
                value="Test Option 1: Customer Complaint"
                control={<Radio color="primary" />}
                label={
                  <Box>
                    <Typography variant="body1" className="scenario-option-title">
                      Test Option 1: Customer Complaint
                    </Typography>
                  </Box>
                }
                className="scenario-option"
              />
              <FormControlLabel
                value="Test Option 2: Feature Request"
                control={<Radio color="primary" />}
                label={
                  <Box>
                    <Typography variant="body1" className="scenario-option-title">
                      Test Option 2: Feature Request
                    </Typography>
                  </Box>
                }
                className="scenario-option"
              />
            </>
          )}
        </RadioGroup>
      </Paper>

      {/* Text Answer Section */}
      {selectedScenario && (
        <Paper elevation={1} className="text-answer-section">
          <Box className="answer-header">
            <Typography variant="h6" className="answer-title">
              Your Response for: {selectedScenario}
            </Typography>
          </Box>

          {/* Rich Text Editor */}
          <RichTextAnswer
            question={{
              ...question,
              text: `Please provide your response for the selected scenario: ${selectedScenario}`
            }}
            answer={{
              text: answer?.text || '',
              images: answer?.images || []
            }}
            onAnswerChange={handleTextChange}
            disabled={disabled}
            examCode={examCode}
            userId={userId}
          />
        </Paper>
      )}

      {/* Instructions */}
      {!selectedScenario && (
        <Paper elevation={1} className="instructions-paper">
          <Typography variant="body2" color="textSecondary">
            Please select a scenario above to begin your response. Each scenario has specific evaluation criteria that will be used to assess your answer.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ScenarioTextAnswer; 